import React, { Component, ErrorInfo, ReactNode } from 'react';
import { useTenant } from '../../contexts/TenantContext';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// Wrapper to access tenant context and pass colors to ErrorBoundary
const ErrorBoundaryWrapper: React.FC<Props> = ({ children, fallback }) => {
  const { config } = useTenant();
  const colors = config?.branding?.colors || {
    text: '#202124',
    accent: '#28a745',
    background: '#ffffff'
  };

  return (
    <ErrorBoundaryClass colors={colors} fallback={fallback}>
      {children}
    </ErrorBoundaryClass>
  );
};

// Main ErrorBoundary class component
class ErrorBoundaryClass extends Component<Props & { colors: any }, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  public render() {
    const { colors } = this.props;

    if (this.state.hasError) {
      // Custom fallback UI
      return this.props.fallback || (
        <div className="min-h-screen flex flex-col items-center justify-center p-4" style={{ backgroundColor: `${colors.background}10` }}>
          <div className="bg-white rounded-xl shadow-sm p-8 max-w-lg w-full text-center">
            <h2 className="text-2xl font-bold mb-4" style={{ color: colors.text }}>
              Something went wrong
            </h2>
            <p className="mb-6" style={{ color: `${colors.text}99` }}>
              We're sorry, but there was an error loading this page. Please try refreshing or contact support if the problem persists.
            </p>
            <div className="text-sm mb-6 p-4 rounded-lg" style={{ backgroundColor: `${colors.background}10`, color: `${colors.text}99` }}>
              Error: {this.state.error?.message || 'Unknown error'}
            </div>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 rounded-lg transition-colors"
              style={{ 
                backgroundColor: colors.accent,
                color: '#ffffff'
              }}
              onMouseOver={(e) => {
                (e.target as HTMLButtonElement).style.backgroundColor = `${colors.accent}dd`;
              }}
              onMouseOut={(e) => {
                (e.target as HTMLButtonElement).style.backgroundColor = colors.accent;
              }}
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundaryWrapper;
